import { useState } from 'react';
import { Dialog, api, useToast } from './_barrel.js';


        function NewBusinessDialog({ token, onClose, onSuccess }) {
            const [mode, setMode] = useState('search');  // 'search' or 'manual'
            const [query, setQuery] = useState('');
            const [hits, setHits] = useState(null);
            const [searching, setSearching] = useState(false);
            const [pickedNumber, setPickedNumber] = useState(null);
            const [pickedTitle, setPickedTitle] = useState('');
            const [purpose, setPurpose] = useState('');
            const [lia, setLia] = useState('');
            const [clientTag, setClientTag] = useState('');
            const [submitting, setSubmitting] = useState(false);
            const [error, setError] = useState(null);
            // Manual fields
            const [manualLegalName, setManualLegalName] = useState('');
            const [manualNumber, setManualNumber] = useState('');
            const [manualJurisdiction, setManualJurisdiction] = useState('GB');
            const [primaryDomain, setPrimaryDomain] = useState('');
            // Worldwide (GLEIF) fields
            const [gleifQuery, setGleifQuery] = useState('');
            const [gleifHits, setGleifHits] = useState(null);
            const [gleifSearching, setGleifSearching] = useState(false);
            const [gleifPicked, setGleifPicked] = useState(null);
            const toast = useToast();

            const runGleifSearch = async (e) => {
                e?.preventDefault();
                if (gleifQuery.trim().length < 2) return;
                setGleifSearching(true); setError(null); setGleifHits(null);
                try {
                    const r = await api(`/api/v1/businesses/gleif/search?q=${encodeURIComponent(gleifQuery)}`, {}, token);
                    setGleifHits(r);
                } catch (e) { setError(e.message); }
                finally { setGleifSearching(false); }
            };

            const submitGleif = async () => {
                if (!gleifPicked || !purpose.trim()) {
                    setError('Pick a company and enter a processing purpose.');
                    return;
                }
                setSubmitting(true); setError(null);
                try {
                    await api('/api/v1/businesses', {
                        method: 'POST',
                        body: JSON.stringify({
                            legal_name: gleifPicked.legal_name,
                            company_number: gleifPicked.lei || null,   // LEI as the registry id
                            jurisdiction: (gleifPicked.jurisdiction || gleifPicked.country || 'XX').slice(0, 8).toUpperCase(),
                            primary_domain: primaryDomain.trim() || null,
                            processing_purpose: purpose.trim(),
                            lia_reference: lia.trim() || null,
                            client_tag: clientTag.trim() || null,
                            notes: gleifPicked.address ? `GLEIF: ${gleifPicked.address}` : null,
                        }),
                    }, token);
                    toast(`Added ${gleifPicked.legal_name}`);
                    onSuccess?.(); onClose();
                } catch (e) { setError(e.message); }
                finally { setSubmitting(false); }
            };

            const runSearch = async (e) => {
                e?.preventDefault();
                if (query.trim().length < 2) return;
                setSearching(true); setError(null); setHits(null);
                try {
                    const r = await api(`/api/v1/businesses/ch/search?q=${encodeURIComponent(query)}`, {}, token);
                    setHits(r);
                } catch (e) { setError(e.message); }
                finally { setSearching(false); }
            };

            const submitFromCH = async () => {
                if (!pickedNumber || !purpose.trim()) {
                    setError('Pick a company and enter a processing purpose.');
                    return;
                }
                setSubmitting(true); setError(null);
                try {
                    await api('/api/v1/businesses/from-ch', {
                        method: 'POST',
                        body: JSON.stringify({
                            company_number: pickedNumber,
                            processing_purpose: purpose.trim(),
                            lia_reference: lia.trim() || null,
                            client_tag: clientTag.trim() || null,
                        }),
                    }, token);
                    toast(`Added ${pickedTitle}`);
                    onSuccess?.(); onClose();
                } catch (e) { setError(e.message); }
                finally { setSubmitting(false); }
            };

            const submitManual = async () => {
                if (!manualLegalName.trim() || !purpose.trim()) {
                    setError('Legal name and processing purpose are required.');
                    return;
                }
                setSubmitting(true); setError(null);
                try {
                    await api('/api/v1/businesses', {
                        method: 'POST',
                        body: JSON.stringify({
                            legal_name: manualLegalName.trim(),
                            company_number: manualNumber.trim() || null,
                            jurisdiction: manualJurisdiction.trim().toUpperCase(),
                            primary_domain: primaryDomain.trim() || null,
                            processing_purpose: purpose.trim(),
                            lia_reference: lia.trim() || null,
                            client_tag: clientTag.trim() || null,
                        }),
                    }, token);
                    toast(`Added ${manualLegalName.trim()}`);
                    onSuccess?.(); onClose();
                } catch (e) { setError(e.message); }
                finally { setSubmitting(false); }
            };

            return (
                <Dialog onClose={onClose} title="Add Business" wide>
                    <div className="flex gap-1 mb-6 border-b border-veil">
                        <button onClick={() => setMode('search')}
                            className="px-5 py-3 font-mono text-xs uppercase tracking-widest"
                            style={{color: mode==='search' ? 'var(--gold)' : 'var(--muted)',
                                    borderBottom: mode==='search' ? '2px solid var(--gold)' : '2px solid transparent',
                                    marginBottom: '-1px'}}>
                            From Companies House (GB)
                        </button>
                        <button onClick={() => setMode('gleif')}
                            className="px-5 py-3 font-mono text-xs uppercase tracking-widest"
                            style={{color: mode==='gleif' ? 'var(--gold)' : 'var(--muted)',
                                    borderBottom: mode==='gleif' ? '2px solid var(--gold)' : '2px solid transparent',
                                    marginBottom: '-1px'}}>
                            Worldwide (GLEIF)
                        </button>
                        <button onClick={() => setMode('manual')}
                            className="px-5 py-3 font-mono text-xs uppercase tracking-widest"
                            style={{color: mode==='manual' ? 'var(--gold)' : 'var(--muted)',
                                    borderBottom: mode==='manual' ? '2px solid var(--gold)' : '2px solid transparent',
                                    marginBottom: '-1px'}}>
                            Manual entry
                        </button>
                    </div>

                    {mode === 'gleif' && (
                        <div className="space-y-4">
                            <div className="text-xs text-muted">Search the worldwide legal-entity registry (GLEIF) — any company outside the UK. Pick a match, add a domain and processing purpose, and screening runs the same as for a UK company.</div>
                            <form onSubmit={runGleifSearch} className="flex gap-2">
                                <input className="flex-1" placeholder="Company name (any country)…" value={gleifQuery}
                                       onChange={e => setGleifQuery(e.target.value)} />
                                <button type="submit" className="btn-gold" disabled={gleifSearching || gleifQuery.trim().length < 2}>
                                    {gleifSearching ? 'Searching…' : 'Search'}
                                </button>
                            </form>
                            {gleifHits && gleifHits.length === 0 && <div className="text-sm text-muted">No matches in the LEI registry.</div>}
                            {gleifHits && gleifHits.length > 0 && (
                                <div className="space-y-2 max-h-72 overflow-y-auto">
                                    {gleifHits.map(h => (
                                        <div key={h.lei}
                                             onClick={() => setGleifPicked(h)}
                                             style={{padding:'8px 12px', cursor:'pointer',
                                                     border: gleifPicked && gleifPicked.lei === h.lei ? '1px solid var(--gold)' : '1px solid var(--veil)',
                                                     background: gleifPicked && gleifPicked.lei === h.lei ? 'rgba(201,169,110,0.08)' : 'transparent'}}>
                                            <div className="text-sm">{h.legal_name}</div>
                                            <div className="text-xs text-muted font-mono">
                                                {[h.jurisdiction || h.country, h.status].filter(Boolean).join(' · ')}
                                                {h.lei && ` · LEI ${h.lei}`}
                                            </div>
                                            {h.address && <div className="text-xs text-muted font-mono">{h.address}</div>}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {gleifPicked && (
                                <div className="space-y-3 pt-2 border-t border-veil">
                                    <div>
                                        <label>Primary domain (optional, powers exposure &amp; media screening)</label>
                                        <input value={primaryDomain} onChange={e => setPrimaryDomain(e.target.value)} placeholder="acme.com" />
                                    </div>
                                    <div>
                                        <label>Processing purpose *</label>
                                        <input value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="e.g. Pre-engagement due diligence" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><label>LIA reference</label><input value={lia} onChange={e => setLia(e.target.value)} /></div>
                                        <div><label>Client tag</label><input value={clientTag} onChange={e => setClientTag(e.target.value)} /></div>
                                    </div>
                                    <button className="btn-gold" onClick={submitGleif} disabled={submitting || !purpose.trim()}>
                                        {submitting ? 'Adding…' : `Add ${gleifPicked.legal_name.slice(0, 40)}`}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {mode === 'search' && (
                        <div className="space-y-4">
                            <form onSubmit={runSearch} className="flex gap-2">
                                <input className="flex-1" placeholder="Company name to search…" value={query}
                                       onChange={e => setQuery(e.target.value)} />
                                <button type="submit" className="btn-gold" disabled={searching || query.trim().length < 2}>
                                    {searching ? 'Searching…' : 'Search'}
                                </button>
                            </form>
                            {hits && hits.length === 0 && (
                                <div className="text-sm text-muted">No matches.</div>
                            )}
                            {hits && hits.length > 0 && (
                                <div className="space-y-2 max-h-72 overflow-y-auto">
                                    {hits.map(h => (
                                        <div key={h.company_number}
                                             onClick={() => { setPickedNumber(h.company_number); setPickedTitle(h.title); }}
                                             style={{
                                                 padding: '8px 12px',
                                                 cursor: 'pointer',
                                                 border: pickedNumber === h.company_number ? '1px solid var(--gold)' : '1px solid var(--veil)',
                                                 background: pickedNumber === h.company_number ? 'rgba(201,169,110,0.08)' : 'transparent',
                                             }}>
                                            <div className="text-sm">{h.title}</div>
                                            <div className="text-xs text-muted font-mono">
                                                {h.company_number}
                                                {h.company_status && ` · ${h.company_status}`}
                                                {h.date_of_creation && ` · incorporated ${h.date_of_creation}`}
                                            </div>
                                            {h.address_snippet && (
                                                <div className="text-xs text-muted font-mono">{h.address_snippet}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {pickedNumber && (
                                <div className="space-y-3 pt-3 border-t border-veil">
                                    <div className="text-xs font-mono uppercase tracking-widest gold">
                                        Selected: {pickedTitle} ({pickedNumber})
                                    </div>
                                    <div>
                                        <label>Processing Purpose *</label>
                                        <textarea rows="2" value={purpose} onChange={e => setPurpose(e.target.value)}
                                            placeholder="e.g. Due diligence ahead of investment review" />
                                    </div>
                                    <div>
                                        <label>LIA Reference (optional)</label>
                                        <input value={lia} onChange={e => setLia(e.target.value)} placeholder="LIA-2026-XXX" />
                                    </div>
                                    <div>
                                        <label>Client / Engagement (optional)</label>
                                        <input value={clientTag} onChange={e => setClientTag(e.target.value)}
                                            placeholder="e.g. Purple Protection 2026" />
                                    </div>
                                </div>
                            )}

                            {error && <div className="text-sm" style={{color:'var(--red)'}}>{error}</div>}
                            <div className="flex justify-end gap-2 pt-2">
                                <button className="btn-ghost" onClick={onClose}>Cancel</button>
                                <button className="btn-gold" onClick={submitFromCH} disabled={!pickedNumber || submitting}>
                                    {submitting ? 'Adding…' : 'Add Business'}
                                </button>
                            </div>
                        </div>
                    )}

                    {mode === 'manual' && (
                        <div className="space-y-4">
                            <div>
                                <label>Legal Name *</label>
                                <input value={manualLegalName} onChange={e => setManualLegalName(e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label>Company Number (optional)</label>
                                    <input value={manualNumber} onChange={e => setManualNumber(e.target.value)} />
                                </div>
                                <div>
                                    <label>Jurisdiction (any country)</label>
                                    <input value={manualJurisdiction} onChange={e => setManualJurisdiction(e.target.value)}
                                        placeholder="GB · US · FR · AE …" maxLength={8} />
                                </div>
                            </div>
                            <div>
                                <label>Primary domain (optional, powers exposure &amp; media screening)</label>
                                <input value={primaryDomain} onChange={e => setPrimaryDomain(e.target.value)} placeholder="acme.com" />
                            </div>
                            <div>
                                <label>Processing Purpose *</label>
                                <textarea rows="2" value={purpose} onChange={e => setPurpose(e.target.value)} />
                            </div>
                            <div>
                                <label>LIA Reference (optional)</label>
                                <input value={lia} onChange={e => setLia(e.target.value)} placeholder="LIA-2026-XXX" />
                            </div>
                            <div>
                                <label>Client / Engagement (optional)</label>
                                <input value={clientTag} onChange={e => setClientTag(e.target.value)}
                                    placeholder="e.g. Purple Protection 2026" />
                            </div>

                            {error && <div className="text-sm" style={{color:'var(--red)'}}>{error}</div>}
                            <div className="flex justify-end gap-2 pt-2">
                                <button className="btn-ghost" onClick={onClose}>Cancel</button>
                                <button className="btn-gold" onClick={submitManual} disabled={submitting}>
                                    {submitting ? 'Adding…' : 'Add Business'}
                                </button>
                            </div>
                        </div>
                    )}
                </Dialog>
            );
        }

export { NewBusinessDialog };
